import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, map, switchMap } from 'rxjs/operators';

import {
    createTask,
    createTaskFailure,
    createTaskSuccess,
    deleteTicketCategoryFailure,
    loadTicketTaskNoteHistory,
    loadTicketTaskNoteHistoryFailure,
    loadTicketTaskNoteHistorySuccess,
    loadTicketTasks,
    loadTicketTasksFailure,
    loadTicketTasksSuccess,
    updateTask,
    updateTaskFailure,
    updateTaskNote,
    updateTaskNoteFailure,
    updateTaskNoteSuccess,
    updateTaskSuccess
} from 'src/app/root-store/ticket-store/actions/tickets.actions';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';
import { HttpResponseHelpers } from 'src/app/shared/helpers';

import { TicketService } from 'src/app/travelx/tickets/ticket.service';

@Injectable()
export class TicketTasksEffects {
    constructor(private readonly _actions$: Actions, private readonly _ticketService: TicketService) {}

    getTicketTasks$ = createEffect(() =>
        this._actions$.pipe(
            ofType(loadTicketTasks, createTaskSuccess, updateTaskSuccess, updateTaskNoteSuccess),
            switchMap((action) => {
                return this._ticketService.getTicketTasks(action.ticketId).pipe(
                    map((tasks) => loadTicketTasksSuccess({ tasks })),
                    catchError((error) =>
                        of(loadTicketTasksFailure({ error: HttpResponseHelpers.parseErrorResponse(error) }))
                    )
                );
            })
        )
    );

    getTicketTaskNoteHistory$ = createEffect(() =>
        this._actions$.pipe(
            ofType(loadTicketTaskNoteHistory, updateTaskNoteSuccess),
            switchMap((action) => {
                return this._ticketService.getTicketTaskNoteHistory(action.ticketId).pipe(
                    map((notes) => loadTicketTaskNoteHistorySuccess({ noteHistory: notes })),
                    catchError((error) =>
                        of(loadTicketTaskNoteHistoryFailure({ error: HttpResponseHelpers.parseErrorResponse(error) }))
                    )
                );
            })
        )
    );

    createTicketTask$ = createEffect(() =>
        this._actions$.pipe(
            ofType(createTask),
            switchMap((action) =>
                this._ticketService
                    .createTicketTask(action.ticketId, action.assigneeId, action.description, action.dueDate)
                    .pipe(
                        map(() =>
                            createTaskSuccess({
                                ticketId: action.ticketId,
                                message: 'Created task successfully'
                            })
                        ),
                        catchError((error) =>
                            of(createTaskFailure({ error: HttpResponseHelpers.parseErrorResponse(error) }))
                        )
                    )
            )
        )
    );

    updateTicketTask$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateTask),
            switchMap((action) =>
                this._ticketService
                    .updateTicketTask(
                        action.updateArgs.ticketId,
                        action.updateArgs.assigneeId,
                        action.updateArgs.description,
                        action.updateArgs.dueDate as Date,
                        action.updateArgs.taskId
                    )
                    .pipe(
                        map(() =>
                            updateTaskSuccess({
                                ticketId: action.updateArgs.ticketId,
                                message: 'Updated task successfully'
                            })
                        ),
                        catchError((error) =>
                            of(updateTaskFailure({ error: HttpResponseHelpers.parseErrorResponse(error) }))
                        )
                    )
            )
        )
    );

    updateTaskNote$ = createEffect(() =>
        this._actions$.pipe(
            ofType(updateTaskNote),
            switchMap((action) =>
                this._ticketService.updateTicketTaskNote(action.update).pipe(
                    map(() =>
                        updateTaskNoteSuccess({
                            ticketId: action.update.ticketId,
                            message: 'Updated task note successfully'
                        })
                    ),
                    catchError((error) =>
                        of(updateTaskNoteFailure({ error: HttpResponseHelpers.parseErrorResponse(error) }))
                    )
                )
            )
        )
    );

    showMessage$ = createEffect(() =>
        this._actions$.pipe(
            ofType(createTaskSuccess, updateTaskSuccess, updateTaskNoteSuccess),
            map((action) => showToasterMessage({ message: action.message }))
        )
    );

    showMessageFailure$ = createEffect(() =>
        this._actions$.pipe(
            ofType(createTaskFailure, updateTaskFailure, updateTaskNoteFailure, deleteTicketCategoryFailure),
            map((action) => showToasterMessage({ message: action.error as string }))
        )
    );
}
